import { useEffect, useRef, useState } from "react";

// ─── DATA ────
const leaders = [
  { name: "Dr. Sukanta",           role: "CEO",                             region: "UK",        initials: "DS", image: "/leaders/CEO.png",        linkedin: "https://www.linkedin.com/in/drsukantakumarjena" },
  { name: "Gareth Stuart Brown",   role: "Business Development Advisor",    region: "UK",        initials: "GB", image: "/leaders/gareth.png",     linkedin: "" },
  { name: "Kanika Reswal",         role: "Director",                        region: "Singapore", initials: "KR", image: "/leaders/kanika.png",     linkedin: "https://www.linkedin.com/in/kanika-reswal-b327a8397/" },
  { name: "Nisha Tiwari",          role: "Chief Operating Officer",         region: "India",     initials: "NT", image: "/leaders/Nisha.jpeg",     linkedin: "" },
  { name: "Peekay",                role: "Director",                        region: "Malaysia",  initials: "PK", image: "/leaders/peekay.png",     linkedin: "" },
  { name: "Raghu Marepalli",       role: "Global Delivery Head",            region: "Global",    initials: "GN", image: "/leaders/Raghu.jpeg",     linkedin: "" },
  { name: "Cason Le",              role: "Chief Technology Officer ",       region: "Singapore", initials: "CL", image: "/leaders/Cason.jpeg",     linkedin: "" },
  { name: "Kriti Jena",            role: "Marketing Director",              region: "India",     initials: "KJ", image: "/leaders/Kriti_Jena.png", linkedin: "https://www.linkedin.com/in/kriti-jena-711334279/" },
];

const coreValues = [
  { icon: "🔍", title: "Curiosity Delivers Growth",  text: "We are endlessly curious — asking questions is the surest way to growth. We never stop looking for better ways to do things. Innovation and continuous learning are part of our corporate DNA." },
  { icon: "⚖️", title: "Integrity is Life",          text: "We take the standards we set very seriously. We are fully committed to being honest with prospects, clients, employees, authorities and all other stakeholders. Everything we do is fair, transparent, and ethical." },
  { icon: "🤝", title: "Relationships Win",          text: "We invest deeply in the relationships we build — with clients, partners, and our people. We believe long-term trust and collaboration are the foundations of lasting business success." },
  { icon: "🎯", title: "Obsess Over Results",        text: "We are relentlessly focused on delivering measurable outcomes. Our success lies in what we actually achieve for our clients, not what we promise on a slide deck." },
  { icon: "⚙️", title: "Processes Empower",         text: "We know that disciplined, well-designed processes are what allow teams to scale, innovate, and deliver reliably — especially in mission-critical environments where failure is not an option." },
];

const milestones = [
  { year: "2015", event: "Inovaantage founded with a focus on GIS solutions for utilities" },
  { year: "2017", event: "Expanded to APAC — Singapore and Malaysia operations established" },
  { year: "2019", event: "Launched rUNr® — proprietary ESRI Utility Network migration platform" },
  { year: "2020", event: "ISO 27001 certification achieved; UK operations established" },
  { year: "2021", event: "ConduitPro® launched; Australia office opened in Sydney" },
  { year: "2022", event: "Global team exceeds 200 engineers; 10+ countries served" },
  { year: "2023", event: "Fueerza & SLD Platform launched; expanded to US market" },
  { year: "2024", event: "Leading provider of Utility Network migration solutions globally" },
];

// ─── LEADER CARD (sub-component, defined inline) ───

function LeaderCard({ leader, index, refCallback }) {
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div className="lc-card" ref={refCallback} data-index={index}>

      {/* animated shine sweep — CSS keyframe fires on :hover */}
      <div className="lc-shine"       aria-hidden="true" />
      {/* slow scan line — always running */}
      <div className="lc-scan"        aria-hidden="true" />
      {/* glowing border pulse — CSS fires on :hover */}
      <div className="lc-glow-border" aria-hidden="true" />

      {/* sequential index badge top-right */}
      <span className="lc-index">{String(index + 1).padStart(2, "0")}</span>

      {/* ── PHOTO ── */}
      <div className="lc-photo-wrap">
        <div className="lc-photo-bg" />
        {leader.image && !imgFailed ? (
          <img
            src={leader.image}
            alt={leader.name}
            className="lc-photo"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="lc-initials">{leader.initials}</div>
        )}
        {/* gradient fade photo into card body */}
        <div className="lc-photo-fade" aria-hidden="true" />
        {/* corner bracket decorators */}
        <i className="lc-brk lc-brk--tl" aria-hidden="true" />
        <i className="lc-brk lc-brk--br" aria-hidden="true" />
      </div>

      {/* ── INFO — fixed-height panel so all cards are equal ── */}
      <div className="lc-info">
        <div className="lc-name">{leader.name}</div>
        <div className="lc-role">{leader.role}</div>
        <div className="lc-divider" />
        <div className="lc-region">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round" aria-hidden="true">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          {leader.region}
        </div>
        {/* explicit spacer — pushes LinkedIn to card bottom */}
        <div className="lc-spacer" />
        <a
          href={leader.linkedin || "https://www.linkedin.com"}
          className={`lc-linkedin${!leader.linkedin ? " lc-linkedin--disabled" : ""}`}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${leader.name} on LinkedIn`}
          onClick={e => {
            if (!leader.linkedin) e.preventDefault();
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
          LinkedIn
        </a>
      </div>
    </div>
  );
}

// ─── LEADERSHIP SECTION ───

function LeadershipSection({ leaders = [] }) {
  const sectionRef = useRef(null);
  const headerRef  = useRef(null);
  const wipeRef    = useRef(null);
  const cardsRef   = useRef([]);

  const setCardRef = (el, i) => { cardsRef.current[i] = el; };

  /* ── ScrollTrigger entrance animations ── */
  useEffect(() => {
    let ctx;
    const init = async () => {
      const { gsap }          = await import("gsap");
      const { ScrollTrigger } = await import("gsap/ScrollTrigger");
      gsap.registerPlugin(ScrollTrigger);

      ctx = gsap.context(() => {
        // header fade-up
        gsap.fromTo(headerRef.current,
          { y: 48, opacity: 0 },
          { y: 0, opacity: 1, duration: 1, ease: "power3.out",
            scrollTrigger: { trigger: sectionRef.current, start: "top 82%" } }
        );
        // wipe line scaleX
        gsap.fromTo(wipeRef.current,
          { scaleX: 0, transformOrigin: "left center" },
          { scaleX: 1, duration: 1.5, ease: "expo.out",
            scrollTrigger: { trigger: sectionRef.current, start: "top 82%" } }
        );
        // cards staggered clipPath wipe
        gsap.fromTo(cardsRef.current.filter(Boolean),
          { y: 60, opacity: 0, clipPath: "inset(0 0 100% 0)" },
          { y: 0, opacity: 1, clipPath: "inset(0 0 0% 0)",
            duration: 0.85, ease: "power4.out", stagger: 0.1,
            scrollTrigger: { trigger: sectionRef.current, start: "top 74%" } }
        );
      }, sectionRef);
    };
    init();
    return () => ctx && ctx.revert();
  }, [leaders]);

  /* ── Magnetic 3D tilt on hover ── */
  const onMouseMove = (e, i) => {
    const card = cardsRef.current[i];
    if (!card) return;
    const { left, top, width, height } = card.getBoundingClientRect();
    const dx = (e.clientX - left - width  / 2) / (width  / 2);
    const dy = (e.clientY - top  - height / 2) / (height / 2);
    import("gsap").then(({ gsap }) => {
      gsap.to(card, {
        rotateY: dx * 10, rotateX: -dy * 8, y: -8, scale: 1.025,
        transformPerspective: 1000, ease: "power1.out",
        duration: 0.22, overwrite: "auto",
      });
    });
  };

  const onMouseLeave = (i) => {
    const card = cardsRef.current[i];
    if (!card) return;
    import("gsap").then(({ gsap }) => {
      gsap.to(card, {
        rotateY: 0, rotateX: 0, y: 0, scale: 1,
        transformPerspective: 1000, ease: "elastic.out(1, 0.5)",
        duration: 0.85, overwrite: "auto",
      });
    });
  };

  return (
    <>
      <style>{`
        @keyframes lc-dot-pulse   { 0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(0,212,255,.5);}50%{opacity:.4;box-shadow:0 0 0 7px rgba(0,212,255,0);} }
        @keyframes lc-shimmer     { 0%{background-position:-250% center;}100%{background-position:250% center;} }
        @keyframes lc-flicker     { 0%,98%,100%{opacity:1;}99%{opacity:.75;} }
        @keyframes lc-scan        { 0%{top:-30%;}100%{top:130%;} }
        @keyframes lc-shine       { 0%{left:-120%;}100%{left:130%;} }
        @keyframes lc-border-glow { 0%,100%{opacity:.55;}50%{opacity:1;} }

        /* section */
        .ls-section    { position:relative; background:var(--bg-dark); padding:110px 0 130px; overflow:hidden; }
        .ls-bg-dots    { position:absolute; inset:0; pointer-events:none; background-image:radial-gradient(rgba(0,212,255,.065) 1px,transparent 1px); background-size:30px 30px; mask-image:radial-gradient(ellipse 88% 72% at 50% 50%,black 25%,transparent 100%); -webkit-mask-image:radial-gradient(ellipse 88% 72% at 50% 50%,black 25%,transparent 100%); }
        .ls-bg-orb-tl  { position:absolute; top:-220px; left:-220px; width:580px; height:580px; border-radius:50%; background:radial-gradient(circle,rgba(0,212,255,.07) 0%,transparent 65%); pointer-events:none; }
        .ls-bg-orb-br  { position:absolute; bottom:-180px; right:-180px; width:480px; height:480px; border-radius:50%; background:radial-gradient(circle,rgba(0,184,160,.06) 0%,transparent 65%); pointer-events:none; }
        .ls-inner      { max-width:1200px; margin:0 auto; padding:0 48px; position:relative; z-index:1; }

        /* header */
        .ls-header     { margin-bottom:72px; }
        .ls-eyebrow    { display:inline-flex; align-items:center; gap:10px; font-family:var(--font-mono); font-size:.7rem; font-weight:500; letter-spacing:2.5px; text-transform:uppercase; color:var(--accent-cyan); margin-bottom:24px; }
        .ls-eyebrow-dot{ display:inline-block; width:8px; height:8px; border-radius:50%; background:var(--accent-cyan); animation:lc-dot-pulse 2s ease-in-out infinite; flex-shrink:0; }
        .ls-title-row  { display:grid; grid-template-columns:1fr 1fr; gap:48px; align-items:end; }
        .ls-heading    { font-family:var(--font-display); font-size:clamp(2.4rem,4.5vw,3.8rem); font-weight:800; line-height:1.08; letter-spacing:-1.5px; color:var(--text-primary); margin:0; animation:lc-flicker 10s ease-in-out infinite; }
        .ls-heading em { display:block; font-style:italic; background:linear-gradient(90deg,var(--accent-cyan) 0%,var(--accent-teal) 35%,var(--accent-cyan) 70%,var(--accent-teal) 100%); background-size:250% auto; -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; animation:lc-shimmer 5s linear infinite; }
        .ls-sub        { font-size:1rem; color:var(--text-secondary); line-height:1.78; margin:0; padding-bottom:6px; }
        .ls-wipe-line  { height:1.5px; margin-top:40px; background:var(--gradient-cyan); border-radius:2px; transform:scaleX(0); transform-origin:left center; box-shadow:0 0 14px rgba(0,212,255,.45); }

        /* grid */
        .ls-grid         { display:grid; grid-template-columns:repeat(4,1fr); gap:22px; align-items:stretch; }
        .ls-card-wrapper { display:flex; }

        /* card */
        .lc-card { position:relative; display:flex; flex-direction:column; flex:1; background:rgba(11,22,40,.75); backdrop-filter:blur(16px); -webkit-backdrop-filter:blur(16px); border:1px solid rgba(0,212,255,.14); border-radius:20px; overflow:hidden; cursor:default; will-change:transform; clip-path:inset(0 0 0% 0); transition:border-color .4s ease, box-shadow .4s ease; }
        .lc-card:hover { border-color:rgba(0,212,255,.5); box-shadow:0 0 0 1px rgba(0,212,255,.18), 0 24px 64px rgba(0,0,0,.55), 0 0 50px rgba(0,212,255,.08); }

        /* shine */
        .lc-shine { position:absolute; top:0; bottom:0; width:55%; left:-120%; background:linear-gradient(105deg,transparent 20%,rgba(255,255,255,.055) 50%,transparent 80%); transform:skewX(-15deg); z-index:8; pointer-events:none; }
        .lc-card:hover .lc-shine { animation:lc-shine .65s ease forwards; }
        /* scan */
        .lc-scan  { position:absolute; left:0; right:0; top:-30%; height:28%; background:linear-gradient(180deg,transparent 0%,rgba(0,212,255,.035) 50%,transparent 100%); z-index:7; pointer-events:none; animation:lc-scan 4s linear infinite; }
        /* glow border */
        .lc-glow-border { position:absolute; inset:0; border-radius:inherit; border:1px solid rgba(0,212,255,0); z-index:6; pointer-events:none; transition:border-color .4s ease; }
        .lc-card:hover .lc-glow-border { border-color:rgba(0,212,255,.35); animation:lc-border-glow 1.8s ease-in-out infinite; box-shadow:inset 0 0 18px rgba(0,212,255,.07); }

        /* index badge */
        .lc-index { position:absolute; top:12px; right:14px; font-family:var(--font-mono); font-size:.6rem; font-weight:500; color:rgba(0,212,255,.3); letter-spacing:1px; z-index:9; user-select:none; pointer-events:none; }

        /* photo */
        .lc-photo-wrap  { position:relative; width:100%; aspect-ratio:4/5; overflow:hidden; background:var(--bg-card-2); flex-shrink:0; }
        .lc-photo-bg    { position:absolute; inset:0; background:radial-gradient(ellipse at 50% 110%,rgba(0,212,255,.18) 0%,transparent 65%); z-index:0; pointer-events:none; }
        .lc-photo       { width:100%; height:100%; object-fit:cover; object-position:top center; display:block; position:relative; z-index:1; transition:transform .6s cubic-bezier(.25,.46,.45,.94), filter .5s ease; filter:grayscale(10%) contrast(1.04); }
        .lc-card:hover .lc-photo { transform:scale(1.07); filter:grayscale(0%) contrast(1.08) brightness(1.04); }
        .lc-initials    { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-family:var(--font-display); font-size:2.4rem; font-weight:800; color:var(--bg-dark); background:var(--gradient-cyan); z-index:1; }
        .lc-photo-fade  { position:absolute; bottom:0; left:0; right:0; height:52%; background:linear-gradient(to top,rgba(11,22,40,.9) 0%,transparent 100%); z-index:2; pointer-events:none; }

        /* corner brackets */
        .lc-brk      { position:absolute; display:block; width:13px; height:13px; z-index:3; transition:all .3s ease; opacity:.4; font-style:normal; }
        .lc-card:hover .lc-brk { width:17px; height:17px; opacity:1; }
        .lc-brk--tl  { top:8px; left:8px; border-top:2px solid var(--accent-cyan); border-left:2px solid var(--accent-cyan); }
        .lc-brk--br  { bottom:8px; right:8px; border-bottom:2px solid var(--accent-cyan); border-right:2px solid var(--accent-cyan); }

        /* info panel — fixed height so ALL cards are identical regardless of text length */
        .lc-info        { display:flex; flex-direction:column; padding:16px 16px 16px; position:relative; z-index:5; margin-top:-22px; height:168px; }

        /* name: always 2 lines reserved, clamp overflow */
        .lc-name        { font-family:var(--font-display); font-size:.93rem; font-weight:800; color:white; line-height:1.25;
                          height:calc(.93rem * 1.25 * 5); overflow:hidden; display:-webkit-box; -webkit-box-orient:vertical; -webkit-line-clamp:2; margin-top:10px; }

        /* role: always 2 lines reserved */
        .lc-role        { font-size:.72rem; font-weight:600; color:var(--accent-cyan); line-height:1.35;
                          height:calc(.72rem * 1.35 * 2); overflow:hidden; display:-webkit-box; -webkit-box-orient:vertical; -webkit-line-clamp:2; margin-bottom:8px; }

        .lc-divider     { height:1px; background:var(--border); margin-bottom:8px; flex-shrink:0; }
        .lc-region      { display:inline-flex; align-items:center; gap:5px; font-size:.72rem; color:var(--text-muted); margin-bottom:0; flex-shrink:0; }

        /* spacer pushes LinkedIn to bottom */
        .lc-spacer      { flex:1; }

        /* linkedin button — always pinned at bottom */
        .lc-linkedin    { display:inline-flex; align-items:center; gap:6px; background:rgba(0,119,181,.1); border:1px solid rgba(0,119,181,.28); color:#5ba4f5; font-family:var(--font-body); font-size:.72rem; font-weight:600; padding:6px 12px; border-radius:7px; text-decoration:none; width:fit-content; flex-shrink:0; transition:all .25s ease; cursor:pointer; }
        .lc-linkedin:hover { background:rgba(0,119,181,.22); border-color:rgba(0,119,181,.55); transform:translateY(-2px); box-shadow:0 5px 16px rgba(0,119,181,.2); }
        /* greyed out when no real URL is set yet */
        .lc-linkedin--disabled { opacity:0.4; cursor:not-allowed; pointer-events:none; }

        /* responsive */
        @media(max-width:1100px){ .ls-grid{ grid-template-columns:repeat(3,1fr); } }
        @media(max-width:900px) { .ls-title-row{ grid-template-columns:1fr; gap:16px; } .ls-grid{ grid-template-columns:repeat(2,1fr); gap:16px; } }
        @media(max-width:768px) { .ls-section{ padding:80px 0 90px; } .ls-inner{ padding:0 24px; } .ls-header{ margin-bottom:52px; } }
        @media(max-width:520px) { .ls-section{ padding:64px 0 72px; } .ls-inner{ padding:0 16px; } .ls-grid{ grid-template-columns:1fr; gap:14px; } .lc-card{ transform:none !important; } }
      `}</style>

      <section className="ls-section" ref={sectionRef} aria-labelledby="ls-heading">
        <div className="ls-bg-dots"   aria-hidden="true" />
        <div className="ls-bg-orb-tl" aria-hidden="true" />
        <div className="ls-bg-orb-br" aria-hidden="true" />

        <div className="ls-inner">

          {/* header */}
          <div className="ls-header" ref={headerRef}>
            <div className="ls-eyebrow">
              <span className="ls-eyebrow-dot" aria-hidden="true" />
              Our Leadership
            </div>
            <div className="ls-title-row">
              <h2 className="ls-heading" id="ls-heading">
                The Team Behind<br />
                <em>Our Success</em>
              </h2>
              <p className="ls-sub">
                Experienced leaders spanning technology, operations, and strategy —
                united by a passion for mission-critical delivery.
              </p>
            </div>
            <div className="ls-wipe-line" ref={wipeRef} aria-hidden="true" />
          </div>

          {/* cards */}
          <div className="ls-grid">
            {leaders.map((leader, i) => (
              <div
                key={leader.name}
                className="ls-card-wrapper"
                onMouseMove={e => onMouseMove(e, i)}
                onMouseLeave={() => onMouseLeave(i)}
              >
                <LeaderCard
                  leader={leader}
                  index={i}
                  refCallback={el => setCardRef(el, i)}
                />
              </div>
            ))}
          </div>

        </div>
      </section>
    </>
  );
}

// ─── ABOUT PAGE ────

export default function AboutPage() {
  return (
    <>
      {/* PAGE HERO  */}
      <div className="page-hero">
        <div className="page-hero-inner">
          <div className="section-tag">About Inovaantage</div>
          <h1 className="page-hero-title">
            We Are{" "}
            <span style={{ background: "var(--gradient-cyan)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Inovaantage
            </span>
          </h1>
          <p className="page-hero-sub">
            A seasoned IT solutions company dedicated to providing robust solutions to the
            telecommunications, transportation, and utilities sectors through data-driven
            strategies and innovative technology.
          </p>
        </div>
      </div>

      {/* ABOUT US */}
      <section className="section" style={{ background: "var(--bg-dark)" }}>
        <div className="section-inner">
          <div className="grid-2" style={{ gap: 64, alignItems: "center" }}>
            <div>
              <div className="section-tag">Our Story</div>
              <h2 className="section-title" style={{ fontSize: "2.2rem" }}>
                Building the Future of <span>Critical Infrastructure</span>
              </h2>
              <p style={{ color: "#0a1929", lineHeight: 1.8, marginBottom: 24, fontSize: "1rem" }}>
                We are a seasoned and highly proficient IT solutions company, dedicated to providing robust solutions to the telecommunications, transportation, and utilities sectors. With a strong focus on data-driven strategies, we assist organizations in navigating their digital transformations and enhancing organizational performance.
              </p>
              <p style={{ color: "#0a1929", lineHeight: 1.8, marginBottom: 24, fontSize: "1rem" }}>
                Since our inception, we have made significant strides and established ourselves as a leading name in the IT/OT landscape. With a diverse portfolio of products and services, we deliver unparalleled value through customized, technology-driven, and innovative solutions that exceed expectations.
              </p>
              <p style={{ color: "#0a1929", lineHeight: 1.8, fontSize: "1rem" }}>
                As a premier provider in the utility and telecommunication space, we specialize in implementing GIS, ADMS, SCADA, OSS, BSS, project management, workforce management, and asset management solutions for electric, water, gas, and fibre companies across multiple countries.
              </p>
            </div>
            <div>
              <div className="grid-2" style={{ gap: 20 }}>
                {[
                  { val: "10+",   label: "Countries" },
                  { val: "200+", label: "Engineers" },
                  { val: "100+", label: "Projects Delivered" },
                  { val: "24/7", label: "Global Support" },
                ].map((s, i) => (
                  <div key={i} style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "32px 24px", textAlign: "center" }}>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: "2.5rem", fontWeight: 800, background: "var(--gradient-cyan)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", marginBottom: 8 }}>
                      {s.val}
                    </div>
                    <div style={{ color: "var(--text-secondary)", fontSize: "0.9rem" }}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 20, background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: 24 }}>
                <p style={{ color: "var(--text-secondary)", fontSize: "0.9rem", lineHeight: 1.7, fontStyle: "italic" }}>
                  "Our state-of-the-art tool, rUNr, is designed to revolutionize data migration in the ESRI platform, automating the transition from geometric network to utility network with 100% accuracy and efficiency."
                </p>
                <div style={{ marginTop: 12, fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.9rem" }}>— Inovaantage Leadership</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION  */}
      <section className="section" style={{ background: "var(--bg-card)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
        <div className="section-inner">
          <div className="grid-2" style={{ gap: 48 }}>
            <div className="card" style={{ padding: "40px" }}>
              <div style={{ fontSize: "3rem", marginBottom: 20 }}>🎯</div>
              <div className="section-tag" style={{ marginBottom: 16 }}>Our Mission</div>
              <h3 style={{ fontFamily: "var(--font-display)",color:"#0a1929", fontSize: "1.5rem", fontWeight: 800, marginBottom: 16 }}>Driving Positive Transformation</h3>
              <p style={{ color: "#0a1929", lineHeight: 1.8 }}>
                At Inovaantage, our mission is to drive positive transformation in organizational performance while simultaneously enhancing the quality of life for individuals. We believe businesses have a responsibility to leverage scientific advancements to contribute to the betterment of society.
              </p>
              <p style={{ color: "#0a1929", lineHeight: 1.8, marginTop: 16 }}>
                We are committed to ensuring that technology serves humanity in meaningful ways, ultimately making our planet more sustainable and livable for all.
              </p>
            </div>
            <div className="card" style={{ padding: "40px" }}>
              <div style={{ fontSize: "3rem", marginBottom: 20 }}>🔭</div>
              <div className="section-tag" style={{ marginBottom: 16 }}>Our Vision</div>
              <h3 style={{ fontFamily: "var(--font-display)",color:"#0a1929", fontSize: "1.5rem", fontWeight: 800, marginBottom: 16 }}>Pioneer Agile Business Solutions</h3>
              <p style={{ color: "#0a1929", lineHeight: 1.8 }}>
                We aspire to pioneer agile business solutions that harness the power of technology and innovation. We are driven by our passion for leveraging technological advancements to drive positive transformation in the corporate landscape.
              </p>
              <p style={{ color: "#0a1929", lineHeight: 1.8, marginTop: 16 }}>
                By staying at the forefront of technological advancements, we aim to continually refine and enhance our solutions to serve as more effective enablers for businesses across various industries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CEO'S VISION  */}
      <section className="section" style={{ background: "var(--bg-dark)" }}>
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">CEO's Vision</div>
            <h2 className="section-title">A Message from <span>Leadership</span></h2>
          </div>
          <div className="ceo-vision-card" style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: "48px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 20, right: 40, fontSize: "8rem", fontFamily: "var(--font-display)", color: "rgba(0,212,255,0.05)", lineHeight: 1 }}>"</div>
            <div className="ceo-vision-grid" style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 48, alignItems: "start" }}>
              <div>
                <div style={{ width: "100%", maxWidth: 240, aspectRatio: "3/4", borderRadius: "16px", overflow: "hidden", marginBottom: 20, border: "2px solid var(--accent-cyan)", boxShadow: "0 0 30px rgba(0,212,255,0.2)" }}>
                  <img src="/leaders/CEO.png" alt="Dr. Sukanta" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
                </div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "1.1rem", marginBottom: 4 }}>Dr. Sukanta</div>
                <div style={{ color: "var(--accent-cyan)", fontSize: "0.88rem", marginBottom: 4 }}>CEO, Inovaantage</div>
                <div style={{ color: "var(--text-muted)", fontSize: "0.82rem" }}>United Kingdom</div>
              </div>
              <div>
                <p style={{ color: "var(--text-secondary)", lineHeight: 1.85, marginBottom: 20, fontSize: "1.05rem", fontStyle: "italic" }}>
                  "Dr. Sukanta Kumar Jena is the Founder and CEO of Inovaantage, a global technology company delivering digital transformation solutions across utilities, infrastructure, smart cities, and telecommunications. With over 20 years of experience in geospatial and enterprise technologies, he has led successful engagements with major organizations across the UK, USA, Middle East, and Asia-Pacific."
                </p>
                <p style={{ color: "var(--text-secondary)", lineHeight: 1.85, marginBottom: 20 }}>
                 Under his leadership, Inovaantage has established a strong international presence with offices in Adelaide, Singapore, Kuala Lumpur, Da Nang, Bhubaneswar, Hyderabad, Delhi, London, Abu Dhabi, and Redlands. He is also committed to fostering skill development and creating opportunities for emerging IT talent, with a strong focus on contributing to Odisha’s technology ecosystem.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUES  */}
      <section className="section" style={{ background: "var(--bg-card)", borderTop: "1px solid var(--border)" }}>
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">Core Values</div>
            <h2 className="section-title" style={{ color: "#1A1A1A" }}>What <span>Drives Us</span></h2>
            <p className="section-subtitle">Our values aren't just words on a wall — they shape every decision we make and every solution we deliver.</p>
          </div>
          <div className="values-grid">
            {coreValues.map((v, i) => (
              <div key={i} className="value-card">
                <div className="value-icon">{v.icon}</div>
                <div className="value-title">{v.title}</div>
                <div className="value-text">{v.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LEADERSHIP TEAM  */}
      <LeadershipSection leaders={leaders} />

      {/* TIMELINE */}
      <section className="section" style={{ background: "var(--bg-card)", borderTop: "1px solid var(--border)" }}>
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">Our Journey</div>
            <h2 className="section-title" style={{ color: "#1A1A1A" }}>Growth <span>Milestones</span></h2>
          </div>
          <div style={{ position: "relative", paddingLeft: 40 }}>
            <div style={{ position: "absolute", left: 16, top: 0, bottom: 0, width: 2, background: "var(--gradient-cyan)", opacity: 0.3 }} />
            {milestones.map((m, i) => (
              <div key={i} style={{ position: "relative", marginBottom: 32, display: "flex", alignItems: "flex-start", gap: 24 }}>
                <div style={{ position: "absolute", left: -32, width: 16, height: 16, background: "var(--accent-cyan)", borderRadius: "50%", top: 4, boxShadow: "0 0 12px rgba(0,212,255,0.5)" }} />
                <div style={{ background: "var(--gradient-cyan)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "0.95rem", minWidth: 50, paddingTop: 2 }}>
                  {m.year}
                </div>
                <div style={{ background: "var(--bg-card-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "12px 20px", flex: 1, color: "var(--text-secondary)", fontSize: "0.92rem" }}>
                  {m.event}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GLOBAL PRESENCE & CERTIFICATIONS  */}
      <section className="section" style={{ background: "var(--bg-dark)" }}>
        <div className="section-inner">
          <div className="section-header">
            <div className="section-tag">Global Presence</div>
            <h2 className="section-title">Operating <span>Worldwide</span></h2>
            <p className="section-subtitle">Currently operating in the UK, Australia, Malaysia, Singapore, India, and the USA — committed to expanding our global presence.</p>
          </div>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 48 }}>
            {["🇬🇧 United Kingdom", "🇦🇺 Australia", "🇮🇳 India", "🇸🇬 Singapore", "🇲🇾 Malaysia", "🇺🇸 United States"].map((c, i) => (
              <div key={i} style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "12px 20px", fontWeight: 600, fontSize: "0.9rem" }}>
                {c}
              </div>
            ))}
          </div>
          <div className="certs-row">
            {[
              { icon: "🏆", title: "ISO 27001",      sub: "Information Security Management" },
              { icon: "🛡️", title: "GDPR Compliant", sub: "Data Protection & Privacy" },
              { icon: "⭐", title: "ESRI Partner",   sub: "Authorized GIS Solutions Partner" },
            ].map((c, i) => (
              <div key={i} className="cert-card">
                <div className="cert-icon">{c.icon}</div>
                <div>
                  <div className="cert-title">{c.title}</div>
                  <div className="cert-sub">{c.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
